import React, { useMemo, useEffect, useCallback, useRef } from 'react'
import Translations from '../../../../../translations'
import { translate } from '../../../../../base/localization'
import Table from '../../../../../components/TablePredict'
import { BaseComponentWithContextProps, componentWithContext } from '../../../../../base/customHooks/componentHOC'
import moment from 'moment'
import { useCustomReducer } from '../../../../../base/customHooks'

// Modeller
import { IPaymentPredictDailyRow, IPaymentPredictMonthlyRow, IRequestModel } from '../models/models'

// Servisler
import { GetSuccessFullPredictApi } from '../services/getSuccessFullPredictApi'
import { GetSuccessFullPredictMonthlyApi } from '../services/getSuccessFullPredictMonthlyApi'
import IPSelectBox from '../../../../../components/IPSelectBox'
import { ISelectOption } from '../../../../../components/IPSelectBox/IPSelectBox'
import IPButton from '../../../../../components/IPButton'

interface ISuccessfullAIPredictState {
  data: IPaymentPredictDailyRow[]
  mdata: IPaymentPredictMonthlyRow[]
  textMessage: string
  selectedMonth?: ISelectOption
  selectedYear?: ISelectOption
  selectedYearMonthly?: ISelectOption

  // KPI snapshot: ilk yüklemede set edilir, sonradan butonla fetch edilince değişmez
  kpiTodayDaily?: IPaymentPredictDailyRow | null
  kpiLastMonthly?: IPaymentPredictMonthlyRow | null
}

type Kind = 'up' | 'down' | 'flat' | 'na'

function SuccessfulAIPredict(props: BaseComponentWithContextProps) {
  const t = translate(Translations)

  const getInitialState = (): ISuccessfullAIPredictState => ({
    data: [],
    mdata: [],
    textMessage: '',
    kpiTodayDaily: null,
    kpiLastMonthly: null
  })

  const [state, setState] = useCustomReducer<ISuccessfullAIPredictState>(getInitialState())

  // -------------------------
  // props loading fonksiyonları stabil olsun (loop fix)
  // -------------------------
  const showLoadingRef = useRef(props.showLoading)
  const hideLoadingRef = useRef(props.hideLoading)

  useEffect(() => {
    showLoadingRef.current = props.showLoading
    hideLoadingRef.current = props.hideLoading
  }, [props.showLoading, props.hideLoading])

  const didFetchRef = useRef(false)

  // KPI sadece ilk yüklemede set edilsin
  const didSetKpisRef = useRef(false)

  // -------------------------
  // Helpers
  // -------------------------
  const parseTrNumber = (val?: string | null): number | null => {
    if (!val) return null
    const cleaned = val.replace(/\./g, '').replace(',', '.').replace(/[^\d.-]/g, '')
    const n = Number(cleaned)
    return Number.isFinite(n) ? n : null
  }

  const formatMoneyTr = (n: number): string => {
    return `${n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`
  }

  const formatPercentTr = (n: number): string => {
    return `%${n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  // Tutarlılık (Gerçek, Tahmini’ye göre):
  // Tahmini > Gerçek => hedefin altında kalındı => "azalma"
  // Tahmini < Gerçek => hedef aşıldı => "artış"
  // diff = gerçek - tahmini
  const calcConsistency = (realStr?: string | null, predStr?: string | null) => {
    const real = parseTrNumber(realStr)
    const pred = parseTrNumber(predStr)

    if (real == null || pred == null) {
      return { kind: 'na' as Kind, diff: null as number | null, pct: null as number | null }
    }

    const diff = real - pred
    const pct = pred === 0 ? null : (diff / pred) * 100

    let kind: Kind = 'flat'
    if (diff > 0) kind = 'up'
    else if (diff < 0) kind = 'down'
    else kind = 'flat'

    return { kind, diff, pct }
  }

  // Gerçek değişimi (önceki -> текущий):
  const calcRealChange = (prevRealStr?: string | null, currRealStr?: string | null) => {
    const prev = parseTrNumber(prevRealStr)
    const curr = parseTrNumber(currRealStr)

    if (prev == null || curr == null) {
      return { kind: 'na' as Kind, diff: null as number | null, pct: null as number | null }
    }

    const diff = curr - prev
    const pct = prev === 0 ? null : (diff / prev) * 100

    let kind: Kind = 'flat'
    if (diff > 0) kind = 'up'
    else if (diff < 0) kind = 'down'
    else kind = 'flat'

    return { kind, diff, pct }
  }

  const getPillStyle = (kind: Kind): React.CSSProperties => {
    const base: React.CSSProperties = {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '6px 10px',
      borderRadius: 999,
      fontWeight: 700,
      fontSize: 12,
      lineHeight: '12px',
      whiteSpace: 'nowrap'
    }

    if (kind === 'na') return { ...base, background: '#f1f3f5', color: '#495057' }
    if (kind === 'flat') return { ...base, background: '#e7f5ff', color: '#1864ab' }
    if (kind === 'up') return { ...base, background: '#ebfbee', color: '#2b8a3e', border: '1px solid #2b8a3e22' }
    return { ...base, background: '#fff5f5', color: '#c92a2a', border: '1px solid #c92a2a22' }
  }

  const iconForKind = (kind: Kind): string => {
    if (kind === 'up') return '▲'
    if (kind === 'down') return '▼'
    if (kind === 'flat') return '→'
    return '—'
  }

  const monthList = [
    { label: 'Ocak', value: 1 },
    { label: 'Şubat', value: 2 },
    { label: 'Mart', value: 3 },
    { label: 'Nisan', value: 4 },
    { label: 'Mayıs', value: 5 },
    { label: 'Haziran', value: 6 },
    { label: 'Temmuz', value: 7 },
    { label: 'Ağustos', value: 8 },
    { label: 'Eylül', value: 9 },
    { label: 'Ekim', value: 10 },
    { label: 'Kasım', value: 11 },
    { label: 'Aralık', value: 12 }
  ]

  const yearList = useMemo(() => {
    const currentYear = moment().year()
    const years: ISelectOption[] = []
    for (let i = 2023; i <= currentYear; i++) {
      years.push({ label: i.toString(), value: i })
    }
    return years
  }, [])

  const ConsistencyPill = ({ realStr, predStr }: { realStr?: string | null; predStr?: string | null }) => {
    const { kind, diff, pct } = calcConsistency(realStr, predStr)

    if (kind === 'na' || diff == null) {
      return (
        <span style={getPillStyle('na')}>
          <span style={{ fontSize: 14, lineHeight: '14px' }}>{iconForKind('na')}</span>
          <span>Veri yok</span>
        </span>
      )
    }

    if (kind === 'flat') {
      return (
        <span style={getPillStyle('flat')}>
          <span style={{ fontSize: 14, lineHeight: '14px' }}>{iconForKind('flat')}</span>
          <span>%0,00 (0,00 ₺)</span>
        </span>
      )
    }

    const pctText = pct == null ? '—' : formatPercentTr(Math.abs(pct))
    const diffText = formatMoneyTr(Math.abs(diff))
    const text = kind === 'up' ? `${pctText} artış (${diffText})` : `${pctText} azalma (${diffText})`

    return (
      <span style={getPillStyle(kind)}>
        <span style={{ fontSize: 14, lineHeight: '14px' }}>{iconForKind(kind)}</span>
        <span>{text}</span>
      </span>
    )
  }

  const RealChangePill = ({
    prevRealStr,
    currRealStr
  }: {
    prevRealStr?: string | null
    currRealStr?: string | null
  }) => {
    const { kind, diff, pct } = calcRealChange(prevRealStr, currRealStr)

    if (kind === 'na' || diff == null) {
      return (
        <span style={getPillStyle('na')}>
          <span style={{ fontSize: 14, lineHeight: '14px' }}>{iconForKind('na')}</span>
          <span>—</span>
        </span>
      )
    }

    if (kind === 'flat') {
      return (
        <span style={getPillStyle('flat')}>
          <span style={{ fontSize: 14, lineHeight: '14px' }}>{iconForKind('flat')}</span>
          <span>%0,00 (0,00 ₺)</span>
        </span>
      )
    }

    const pctText = pct == null ? '—' : formatPercentTr(Math.abs(pct))
    const diffText = formatMoneyTr(Math.abs(diff))
    const text = kind === 'up' ? `${pctText} artış (${diffText})` : `${pctText} azalma (${diffText})`

    return (
      <span style={getPillStyle(kind)}>
        <span style={{ fontSize: 14, lineHeight: '14px' }}>{iconForKind(kind)}</span>
        <span>{text}</span>
      </span>
    )
  }

  // -------------------------
  // getLatestDaily: "bugünün tarihine" bakarak seçsin
  // Öncelik:
  // 1) Bugün tarihine eşit olan kayıtlar içinden en güncel saat
  // 2) Bugün yoksa en güncel tarih
  // -------------------------
  const getLatestDaily = useCallback((rows: IPaymentPredictDailyRow[]) => {
    if (!rows?.length) return null

    const today = moment().format('YYYY-MM-DD')
    const todayRows = rows.filter(r => moment(r.Date).format('YYYY-MM-DD') === today)
    const pickFrom = todayRows.length ? todayRows : rows

    let best = pickFrom[0]
    let bestTs = moment(pickFrom[0].Date).valueOf()

    for (let i = 1; i < pickFrom.length; i++) {
      const ts = moment(pickFrom[i].Date).valueOf()
      if (ts > bestTs) {
        best = pickFrom[i]
        bestTs = ts
      }
    }

    return best
  }, [])

  const getLatestMonthly = useCallback((rows: IPaymentPredictMonthlyRow[]) => {
    if (!rows?.length) return null

    for (let i = rows.length - 1; i >= 0; i--) {
      if (rows[i].RealPrice !== null && rows[i].RealPrice !== undefined) {
        return rows[i]
      }
    }

    return null
  }, [])


  // -------------------------
  // API calls (loop fix)
  // -------------------------
  const getPredict = useCallback(async () => {
    try {
      const reqModel: IRequestModel = {
        Month: state.selectedMonth && Number(state.selectedMonth?.value),
        Year: state.selectedYear && Number(state.selectedYear?.value)
      }
      showLoadingRef.current?.()
      const res = await new GetSuccessFullPredictApi({}).getSuccessFullPredict(reqModel)
      if (res) setState({ data: res })
    } catch (err: any) {
      console.log(err)
    } finally {
      hideLoadingRef.current?.()
    }
  }, [setState])

  const getPredictMonthly = useCallback(async () => {
    try {
      const reqModel: IRequestModel = {
        Year: state.selectedYearMonthly && Number(state.selectedYearMonthly?.value)
      }
      setState({ textMessage: 'Yükleniyor...' })
      showLoadingRef.current?.()
      const res = await new GetSuccessFullPredictMonthlyApi({}).getSuccessFullPredict(reqModel)
      if (res) setState({ mdata: res, textMessage: '' })
    } catch (err: any) {
      console.log(err)
      setState({ textMessage: '' })
    } finally {
      hideLoadingRef.current?.()
    }
  }, [setState])

  useEffect(() => {
    if (didFetchRef.current) return
    didFetchRef.current = true

    getPredict()
    getPredictMonthly()
  }, [getPredict, getPredictMonthly])

  useEffect(() => {
    const m = moment()
    const currentMonth = m.month() + 1 // moment: 0-11, bizim list: 1-12
    const currentYear = m.year()

    const monthOption = monthList.find(x => Number(x.value) === currentMonth)
    const yearOption = yearList.find(x => Number(x.value) === currentYear)

    setState({
      selectedMonth: monthOption,
      selectedYear: yearOption,
      selectedYearMonthly: yearOption
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // -------------------------
  // KPI snapshot: ilk dolu veri geldikten sonra bir kere set et
  // Butonla tekrar fetch edilince state.data/mdata değişse bile KPI değişmez
  // -------------------------
  useEffect(() => {
    if (didSetKpisRef.current) return

    const hasDaily = (state.data?.length ?? 0) > 0
    const hasMonthly = (state.mdata?.length ?? 0) > 0

    // İkisi de geldiyse set et (deterministik KPI)
    if (!hasDaily || !hasMonthly) return

    didSetKpisRef.current = true

    setState({
      kpiTodayDaily: getLatestDaily(state.data ?? []),
      kpiLastMonthly: getLatestMonthly(state.mdata ?? [])
    })
  }, [state.data, state.mdata, getLatestDaily, getLatestMonthly, setState])

  // -------------------------
  // Columns
  // -------------------------
  const tableColumnsDaily = useMemo(
    () => [
      { Header: 'Tarih', accessor: 'col1' },
      { Header: 'Gerçekleşen Tutar', accessor: 'col2' },
      { Header: 'Tahmini Tutar', accessor: 'col3' },
      { Header: 'Tutarlılık Raporu', accessor: 'col4' },
      { Header: 'Günlük Değişim (Gerçek)', accessor: 'col5' }
    ],
    []
  )

  const tableColumnsMonthly = useMemo(
    () => [
      { Header: 'Ay', accessor: 'col1' },
      { Header: 'Gerçekleşen Tutar', accessor: 'col2' },
      { Header: 'Tahmini Tutar', accessor: 'col3' },
      { Header: 'Tutarlılık Raporu', accessor: 'col4' },
      { Header: 'Aylık Değişim (Gerçek)', accessor: 'col5' }
    ],
    []
  )

  // -------------------------
  // Data mapping
  // -------------------------
  const tableDataDaily = useMemo(() => {
    const sorted = [...(state.data ?? [])].sort((a, b) => moment(a.Date).valueOf() - moment(b.Date).valueOf())

    const tableData = sorted.map((item, idx) => {
      const real = item.RealPrice ?? null
      const pred = item.PredictPrice ?? null
      const prevReal = idx > 0 ? sorted[idx - 1]?.RealPrice ?? null : null

      return {
        col1: moment(item.Date).format('DD.MM.YYYY'),
        col2: real ? `${real} ₺` : '',
        col3: pred ? `${pred} ₺` : '',
        col4: <ConsistencyPill realStr={real} predStr={pred} />,
        col5: <RealChangePill prevRealStr={prevReal} currRealStr={real} />
      }
    })

    return { tableData }
  }, [state.data])

  const tableDataMonthly = useMemo(() => {
    const tableData = (state.mdata ?? []).map((item, idx) => {
      const real = item.RealPrice ?? null
      const pred = item.PredictPrice ?? null
      const prevReal = idx > 0 ? state.mdata[idx - 1]?.RealPrice ?? null : null

      return {
        col1: item.MonthName,
        col2: real ? `${real} ₺` : '',
        col3: pred ? `${pred} ₺` : '',
        col4: <ConsistencyPill realStr={real} predStr={pred} />,
        col5: <RealChangePill prevRealStr={prevReal} currRealStr={real} />
      }
    })

    return { tableData }
  }, [state.mdata])

  // -------------------------
  // KPI card component
  // -------------------------
  const KpiCard = ({
    title,
    realStr,
    predStr
  }: {
    title: string
    realStr?: string | null
    predStr?: string | null
  }) => (
    <div className='col-lg-6 mb-3'>
      <div className='card h-100' style={{ borderRadius: 12 }}>
        <div className='card-body'>
          <div className='d-flex justify-content-between align-items-start'>
            <div>
              <div style={{ fontSize: 13, color: '#6c757d', fontWeight: 600 }}>{title}</div>

              <div style={{ marginTop: 6 }}>
                <div style={{ fontSize: 12, color: '#868e96' }}>Gerçek</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{realStr ? `${realStr} ₺` : '—'}</div>
              </div>

              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 12, color: '#868e96' }}>Tahmini</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{predStr ? `${predStr} ₺` : '—'}</div>
              </div>

              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 12, color: '#868e96' }}>Gerçek-Tahmini Tutarlılık</div>
                <div style={{ marginTop: 4 }}>
                  <ConsistencyPill realStr={realStr} predStr={predStr} />
                </div>
              </div>
            </div>

            <div style={{ marginLeft: 12 }}>
              <ConsistencyPill realStr={realStr} predStr={predStr} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className=''>
      <div className='content col-lg-12 m-auto'>
        <div className='card'>
          <div className='card-header d-flex justify-content-between align-items-center'>
            <span className='h4 mb-0'>Başarılı İşlemler AI Tahmin</span>
          </div>

          <div className='card-body'>
            <div className='row m-2'>
              <KpiCard
                title='Bugün (Gerçek-Tahmini)'
                realStr={state.kpiTodayDaily?.RealPrice ?? null}
                predStr={state.kpiTodayDaily?.PredictPrice ?? null}
              />
              <KpiCard
                title='Son Ay (Gerçek-Tahmini)'
                realStr={state.kpiLastMonthly?.RealPrice ?? null}
                predStr={state.kpiLastMonthly?.PredictPrice ?? null}
              />
            </div>

            <div className='m-4'>
              <div className='row mb-5'>
                <div className='col-md-2'>
                  <label className='form-label' style={{ fontSize: '0.9rem', fontWeight: '500', color: '#495057' }}>
                    Ay
                  </label>
                  <IPSelectBox
                    placeholder='Ay seçiniz'
                    options={monthList}
                    value={state.selectedMonth}
                    onChangeSingle={option => {
                      setState({ selectedMonth: option })
                    }}
                  />
                </div>
                <div className='col-md-2'>
                  <label className='form-label' style={{ fontSize: '0.9rem', fontWeight: '500', color: '#495057' }}>
                    Yıl
                  </label>
                  <IPSelectBox
                    placeholder='Yıl seçiniz'
                    options={yearList}
                    value={state.selectedYear}
                    onChangeSingle={option => {
                      setState({ selectedYear: option })
                    }}
                  />
                </div>
                <div className='col-md-2 mt-2'>
                  <label className='form-label' style={{ fontSize: '0.9rem', fontWeight: '500', color: '#495057' }}></label>
                  <IPButton
                    text={'Getir'}
                    onClick={() => {
                      getPredict()
                    }}
                  />
                </div>
              </div>
              <span>
                <h5>Günlük AI Tahmin</h5>
              </span>
              <Table
                columns={tableColumnsDaily}
                data={tableDataDaily.tableData}
                currentPage={0}
                columnCount={3}
                backgroundColor='red'
                color='white'
              />
            </div>

            <br />

            <div className='m-4'>
              <div className='row mb-5'>
                <div className='col-md-2'>
                  <label className='form-label' style={{ fontSize: '0.9rem', fontWeight: '500', color: '#495057' }}>
                    Yıl
                  </label>
                  <IPSelectBox
                    placeholder='Yıl seçiniz'
                    options={yearList}
                    value={state.selectedYearMonthly}
                    onChangeSingle={option => {
                      setState({ selectedYearMonthly: option })
                    }}
                  />
                </div>
                <div className='col-md-2 mt-2'>
                  <label className='form-label' style={{ fontSize: '0.9rem', fontWeight: '500', color: '#495057' }}></label>
                  <IPButton
                    text={'Getir'}
                    onClick={() => {
                      getPredictMonthly()
                    }}
                  />
                </div>
              </div>
              <span>
                <h5>Aylık AI Tahmin</h5>
              </span>
              <span>{state.textMessage}</span>

              {state.textMessage !== 'Yükleniyor...' && (
                <Table
                  columns={tableColumnsMonthly}
                  data={tableDataMonthly.tableData}
                  currentPage={0}
                  columnCount={3}
                  backgroundColor='red'
                  color='white'
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default componentWithContext(SuccessfulAIPredict)
